// CSS AST transformations and related utilities, based on the 'css-tree' CSS
// parsing library.
//
// When working with 'css-tree', you may find it useful to visualize the AST
// that will be generated for a certain input. You can do that using AST
// Explorer:
//   https://astexplorer.net/#/gist/244e2fb4da940df52bf0f4b94277db44/e79aff44611020b22cfd9708f3a99ce09b7d67a8

import { assertExhaustive } from '@fullstory/typeutils';
import {
  Atrule,
  Block,
  clone,
  CssNode,
  Declaration,
  DeclarationList,
  FunctionNode,
  generate,
  Identifier,
  // @ts-ignore - TODO currently not an exported type in our css-tree fork
  lexer,
  List,
  ListItem,
  MediaFeature,
  parse,
  Raw,
  StringNode,
  SyntaxParseError,
  Url,
  Value,
  walk,
  WalkContext,
  WalkOptions,
} from '@sethfowler/css-tree';
import { OriginalTagAttr, SimulatedPseudoClass } from './constants';
import { UrlRewriter } from '@fullstory/url-rewriter';
import {
  createAttributeSelector,
  createDeclaration,
  createFunction,
  createIdentifier,
  createRaw,
  createString,
  createTypeSelector,
  createValue,
  Mutated,
  mutatedTag,
  PossiblyMutated,
} from './utils';

export type CSSTreeTransform = Pick<WalkOptions, 'enter' | 'leave' | 'visit'>;


const MaxParseErrors = 16;

// A helper class that can apply transformations to CSS source code. Usage
// follows this pattern:
//   const transformer = new CSSTreeTransformer(input);
//   transformer.run(SomeTransform());
//   const output = transformer.serialize();
export class CSSTreeTransformer {
  private _astRoot: CssNode | undefined;
  private _parsingErrors: number = 0;

  // Construct a CSSTreeTransform for the given input. The optional 'context'
  // parameter allows you to parse the input using some CSS subgrammar instead
  // of treating it as a complete stylesheet. The supported values are here:
  //   https://github.com/csstree/csstree/blob/138c56ccfb7cced18668d38a3b1e1d43e4e22f37/docs/parsing.md#context
  constructor(private _input: string, context: string = 'stylesheet') {
    try {
      this._astRoot = parse(this._input, {
        context,
        parseCustomProperty: true,
        positions: true,
        onParseError: (error: SyntaxParseError): void => {
          this._handleParseError(error);
        },
      });
    } catch (_) {
      console.error('Parsing failed, fallback to unmodified css text input.');
    }
  }

  hadParseFailure(): boolean {
    return this._parsingErrors > 0;
  }

  // Run a single transform in isolation.
  run(transform: CSSTreeTransform): void {
    if (!this._astRoot) return;
    walk(this._astRoot, transform);
  }

  // Run a series of transforms, fused into a single pass over the AST. For each
  // node in the AST, the fused transform calls each of the original transform's
  // visitor methods in the order that they appear in the 'transforms'
  // parameter.
  //
  // It's important to be aware that *not all transforms can be fused*. In
  // particular, care needs to be taken when fusing transforms that add or
  // replace AST nodes; later transforms in the sequence will not get an
  // opportunity to visit those new nodes.
  runFused(transforms: CSSTreeTransform[]): void {
    if (!this._astRoot) return;
    walk(this._astRoot, {
      enter(
        this: WalkContext,
        node: CssNode,
        item: ListItem<CssNode>,
        inList: List<CssNode>,
      ): void {
        for (const transform of transforms) {
          if (transform.enter) {
            transform.enter.call(this, node, item, inList);
          }
        }
      },
      leave(
        this: WalkContext,
        node: CssNode,
        item: ListItem<CssNode>,
        inList: List<CssNode>,
      ): void {
        for (const transform of transforms) {
          if (transform.leave) {
            transform.leave.call(this, node, item, inList);
          }
        }
      },
    });
  }

  // Serialized the transformed AST to a string.
  serialize(): string {
    if (!this._astRoot) return this._input; // parsing failed, return unmodified input
    return interpolateMutations(this._astRoot, this._input);
  }

  private _handleParseError(error: SyntaxParseError): void {
    // The CssTree parser is more restrictive than the actual CSS grammar and
    // rejects some proprietary CSS features. That's OK; they'll just pass
    // through the transforms unmodified. For performance reasons, we want to
    // avoid signaling a parse failure for these cases, since in some cases that
    // can lead us to retry parsing on a slow path, and retrying won't help with
    // this kind of error.
    const source = (error as any).source;
    switch (error.message) {
      case 'Unexpected input':
        // Tolerate legacy IE-only "Visual Filters and Transitions". These were
        // obsolete as of IE 9, but people are still including them in their CSS
        // for IE 8 support. For information about these features, see here:
        //   https://docs.microsoft.com/en-us/previous-versions/windows/internet-explorer/ie-developer/platform-apis/ms532849(v=vs.85)
        if (source.substr(error.offset, 18) === ':DXImageTransform.') {
          console.debug(
            `Ignoring CSS parse error due to IE-only Visual Filters and Transitions.`,
          );
          return;
        }
        break;

      case 'RightParenthesis is expected':
        // 'filter: alpha(opacity=N)' is another style of legacy IE-only filter.
        if (source.substr(error.offset - 13, 14) === 'alpha(opacity=') {
          console.debug(
            `Ignoring CSS parse error due to IE-only alpha() CSS filter.`,
          );
          return;
        }
        break;
    }

    // If we've reached this point, we're dealing with a "real" parse error. Warn
    // and record that parsing failed so we can potentially retry later.
    console.warn(`Error during CSS transform: ${(error as any).formattedMessage}`);
    if (++this._parsingErrors >= MaxParseErrors) {
      throw error;
    }
  }
}

// Returns a version of the given string with '\' escapes evaluated away.
function unescape(escapedString: string): string {
  return escapedString.replace(/\\(.)/g, '$1');
}

// Returns a new string with all unsafe characters for the given quote type
// escaped.
function escapeForQuotes(quote: '"' | '\'' | ')', unescapedString: string): string {
  switch (quote) {
    case `"`:
      return unescapedString.replace(/["\\](?!\n)/g, `\\$&`);
    case `'`:
      return unescapedString.replace(/['\\](?!\n)/g, `\\$&`);
    case `)`:
      // See: https://drafts.csswg.org/css-values/#urls
      // > The unquoted 'url()' syntax has extra escaping requirements: parentheses, whitespace characters, single quotes (')
      // > and double quotes (") appearing in a URL must be escaped with a backslash, e.g. url(open\(parens), url(close\)parens).
      return unescapedString.replace(/[()'" \\](?!\n)/g, `\\$&`);
    default:
      return assertExhaustive(quote, `Unexpected quote character '${quote}'`);
  }
}

// Rewrite a StringNode which is known to contain a URL.
function rewriteStringUrlValue(
  node: PossiblyMutated<StringNode>,
  urlRewriter: UrlRewriter,
): void {
  if (node.value.length <= 2) {
    return;
  }

  // Normalize the URL by removing quotes and evaluating '\' escapes.
  const normalizedValue = unescape(node.value.slice(1, -1));

  // Rewrite it.
  const rewrittenValue = urlRewriter.rewriteUrl(normalizedValue);

  // Escape the URL appropriately for the quote style this StringNode uses.
  const quote = node.value[0] as ('"' | '\'');
  const escapedValue = escapeForQuotes(quote, rewrittenValue);

  // Restore the quotes.
  node.value = `${quote}${escapedValue}${quote}`;

  node[mutatedTag] = true;
}

// Rewrite a Raw node which is known to contain a URL.
function rewriteRawUrlValue(
  node: PossiblyMutated<Raw>,
  urlRewriter: UrlRewriter,
): void {
  // Normalize the URL by evaluating '\' escapes.
  const normalizedValue = unescape(node.value);

  // Rewrite it.
  const rewrittenValue = urlRewriter.rewriteUrl(normalizedValue);

  // Raw URLs live in CSS functions like url() or image(). Escape any ')'
  // characters in the URL so that they don't confuse the CSS parser into
  // thinking that the function has ended.
  const escapedValue = escapeForQuotes(')', rewrittenValue);

  node.value = escapedValue;
  node[mutatedTag] = true;
}

// Rewrite the URL of an @import rule. Note that we only rewrite StringNodes in
// this function; CSSTreeUrlTransform visits all Url nodes, so we handle those
// generically.
function rewriteUrlsInImportAtRule(
  node: Atrule,
  urlRewriter: UrlRewriter,
): void {
  const prelude = node.prelude;
  if (!prelude) {
    return;
  }
  if (prelude.type !== 'AtrulePrelude') {
    return;
  }
  const firstChild = prelude.children.first();
  if (!firstChild) {
    return;
  }
  if (firstChild.type !== 'String') {
    return;
  }
  rewriteStringUrlValue(firstChild, urlRewriter);
}

// Rewrite URLs in an image() or image-set() function. Note that we only rewrite
// StringNodes in this function; CSSTreeUrlTransform visits all Url nodes, so we
// handle those generically.
function rewriteUrlsInImageFunction(
  node: FunctionNode,
  urlRewriter: UrlRewriter,
): void {
  node.children.forEach((child: CssNode): void => {
    if (child.type !== 'String') {
      return;
    }
    rewriteStringUrlValue(child, urlRewriter);
  });
}

// Rewrite a Url node.
function rewriteUrlNode(
  node: Url,
  urlRewriter: UrlRewriter,
): void {
  const urlValue = node.value;
  switch (urlValue.type) {
    case 'String':
      rewriteStringUrlValue(urlValue, urlRewriter);
      break;

    case 'Raw':
      rewriteRawUrlValue(urlValue, urlRewriter);
      break;
  }
}

// A transform that rewrites all URLs in the AST using the provided UrlRewriter.
export function CSSTreeUrlTransform(urlRewriter: UrlRewriter): CSSTreeTransform {
  return {
    enter(this: WalkContext, node: CssNode): void {
      switch (node.type) {
        case 'Atrule':
          if (node.name === 'import') {
            rewriteUrlsInImportAtRule(node, urlRewriter);
          }
          break;

        case 'Function':
          if (node.name === 'image' || node.name === 'image-set') {
            rewriteUrlsInImageFunction(node, urlRewriter);
          }
          break;

        case 'Url':
          rewriteUrlNode(node, urlRewriter);
          break;
      }
    },
  };
}

// A mapping from CSS media features in the original source to the features we'd
// like to use during playback. CSSTreeMediaFeaturesTransform will automatically
// handle 'min-' and 'max-' prefixes, so this map doesn't need to include all
// variants of range features.
const mediaFeatureMappings = new Map<string, string>([
  ['device-width', 'width'],
  ['device-height', 'height'],
  ['device-aspect-ratio', 'aspect-ratio'],
]);

function rewriteMediaFeature(node: PossiblyMutated<MediaFeature>): void {
  // If the feature has a range prefix, pull it out.
  const match = /(min-|max-|)(.+)/.exec(node.name);
  if (!match) {
    return;
  }

  // Remap the feature according to the mapping table.
  const [, rangePrefix, featureName] = match;
  const remappedFeatureName = mediaFeatureMappings.get(featureName!);
  if (!remappedFeatureName) {
    return;
  }

  node.name = `${rangePrefix}${remappedFeatureName}`;
  node[mutatedTag] = true;
}

// A transform that remaps CSS media features to make them suitable for
// playback.
export function CSSTreeMediaFeaturesTransform(): CSSTreeTransform {
  return {
    enter(this: WalkContext, node: CssNode): void {
      if (node.type !== 'MediaFeature') {
        return;
      }
      rewriteMediaFeature(node);
    },
  };
}

// A table of replacements for class selectors.
const classSelectorMappings = new Map<string, CssNode[]>([
  [
    '_fs_focus',
    [createAttributeSelector(SimulatedPseudoClass.Focus)],
  ],
  [
    '_fs_focus-within',
    [createAttributeSelector(SimulatedPseudoClass.FocusWithin)],
  ],
  [
    '_fs_hover',
    [createAttributeSelector(SimulatedPseudoClass.Hover)],
  ],
]);

// A table of replacements for pseudoelement selectors.
const pseudoClassSelectorMappings = new Map<string, CssNode[]>([
  [
    'focus',
    [createAttributeSelector(SimulatedPseudoClass.Focus)],
  ],
  [
    'focus-within',
    [createAttributeSelector(SimulatedPseudoClass.FocusWithin)],
  ],
  [
    'hover',
    [createAttributeSelector(SimulatedPseudoClass.Hover)],
  ],
]);

// A table of replacements for type selectors.
const typeSelectorMappings = new Map<string, CssNode[]>([
  [
    'embed',
    [
      createTypeSelector('img'),
      createAttributeSelector(OriginalTagAttr, '=', createString(`"embed"`)),
    ],
  ],
  [
    'object',
    [
      createTypeSelector('img'),
      createAttributeSelector(OriginalTagAttr, '=', createString(`"object"`)),
    ],
  ],
]);

// Replace an AST node by looking up its 'name' property in the provided table
// of replacements.
function remapNodeByName(
  // Potential replacements.
  mappings: Map<string, CssNode[]>,
  // The node to replace if its 'name' value appears in 'mappings'.
  node: CssNode & { name: string },
  // The list item corresponding to the node.
  item: ListItem<CssNode>,
  // The list in which 'item' appears.
  inList: List<CssNode>,
): void {
  const mapping = mappings.get(node.name);
  if (!mapping) {
    return;
  }

  // We may replace the original node with multiple new AST nodes. Loop over
  // and insert each new node. Note that it's important to copy over the source
  // location from the node we're replacing; interpolateMutations() needs this
  // information to determine what it corresponds to in the original source.
  for (const newNode of mapping) {
    const clonedNode = clone(newNode);
    clonedNode.loc = node.loc;
    inList.insertData(clonedNode, item);
  }

  // Remove the original node now that its replacements are in place.
  inList.remove(item);
}

// A transform that remaps CSS selectors to enable certain playback features.
export function CSSTreeSelectorsTransform(): CSSTreeTransform {
  return {
    enter(
      this: WalkContext,
      node: CssNode,
      item: ListItem<CssNode>,
      inList: List<CssNode>,
    ): void {
      switch (node.type) {
        case 'ClassSelector':
          remapNodeByName(classSelectorMappings, node, item, inList);
          break;
        case 'PseudoClassSelector':
          remapNodeByName(pseudoClassSelectorMappings, node, item, inList);
          break;
        case 'TypeSelector':
          remapNodeByName(typeSelectorMappings, node, item, inList);
          break;
      }
    },
  };
}

// Replay sets box-sizing: border-box for masked elements.
// Rewrite CSS to avoid breaking inheritance.
export function CSSBoxSizingTransform(): CSSTreeTransform {
  function enter(
    this: WalkContext,
    node: CssNode,
    item: ListItem<CssNode>,
    inList: List<CssNode>,
  ): void {
    if (node.type != 'Declaration' || node.property != 'box-sizing') {
      return;
    }

    if (node.value.type == 'Raw') {
      return; // Invalid value or syntax error.
    }

    const ident = node.value.children.first();
    if (!ident) {
      return; // Invalid state.
    }

    // Rewrite CSS to use a variable, --fs-inherit-box-sizing, when
    // it would otherwise directly inherit box-sizing.
    // Then when replay sets box-sizing, it won't break inherited values.
    switch (ident.type) {
      case 'Identifier': {
        switch (ident.name) {
          // Note that box-sizing is not inherited by default.
          case 'inherit': {
            // NOTE (eugenechang) for inherit, we also want to set the variable to inherit
            // because failing to do so can cause other rules to overwrite the value
            // see reftest css-rewrite-box-sizing-multiple-rules.html as an example
            const inheritDecl = createDeclaration('--fs-inherit-box-sizing', createValue(createRaw(ident.name)));
            insertDeclaration(this, inheritDecl, inList, item);
            const decl = createDeclaration('box-sizing', createValue(createRaw('var(--fs-inherit-box-sizing)')));
            insertDeclaration(this, decl, inList, item);
            inList.remove(item);
            break;
          }
          case 'border-box':
          case 'content-box':
          case 'initial':
          case 'revert':
          case 'unset': {
            const decl = createDeclaration('--fs-inherit-box-sizing', createValue(createRaw(ident.name)));
            insertDeclaration(this, decl, inList, item);
            break;
          }
          default:
            // The browser will skip this rule. Don't set the variable.
            console.debug(`Unexpected box-sizing value: ${ident.name}`);
        }
        break;
      }
      case 'Function': {
        // using variables (e.g. value of `var(--some-variable)`) will count as a Function type
        // it is possible, though unlikely, that the value of the function could be invalid,
        // and therefore we overwrite the value of --fs-inherit-box-sizing with an invalid value
        const decl = createDeclaration('--fs-inherit-box-sizing', createValue(ident));
        insertDeclaration(this, decl, inList, item);
        break;
      }
      default:
        // invalid or non handled type
        console.debug(`Unexpected box-sizing value type: ${ident.type}`);
    }
  }

  return { enter };
}

/**
 * Converts a font styles to CSS Custom Properties. Also, explicitly convers 'font' shorthand
 * prop to it's explicit longhand properties.
 *
 * Note: we currently only extract `font-family` longhand property
 *
 */
export function CSSMaskFontTransform(): CSSTreeTransform {
  const fontLongHandProperties = [
    'font-family',
  ] as const;
  return {
    enter(this: WalkContext, node: CssNode, item: ListItem<CssNode>, inList: List<CssNode>): void {
      const ctx = this;

      /** Add declarations to set css custom properties */
      const updateDeclarationToUseCssVar = (node: Declaration) => {
        const property = `--fs-undo-css-${node.property}`;
        const copiedValue = clone(node.value) as Mutated<Raw | Value>;
        copiedValue[mutatedTag] = true;
        const decl = createDeclaration(property, copiedValue);
        insertDeclaration(ctx, decl, inList, item);
      };

      /** Extract longhand properties and set values into css custom properties */
      const convertToLonghand = (node: Declaration, keys: readonly string[]) => {
        const expandedProps = extractLonghandProperties(node, keys);
        if (expandedProps.length === 0) {
          console.warn(`${node.property} property wasn't converted to longhand.`);
        }

        // inject the longhand styles and convert to variables
        for (const { name, value } of expandedProps) {
          const decl = createDeclaration(name, createValue(createRaw(value)));
          updateDeclarationToUseCssVar(decl);
        }
      };

      switch (node.type) {
        case 'Declaration': {
          switch (node.property) {
            case 'font': {
              convertToLonghand(node, fontLongHandProperties);
              break;
            }
            default:
              if (fontLongHandProperties.indexOf(node.property as any) !== -1) {
                updateDeclarationToUseCssVar(node);
              }
          }
        }
      }
    },
  };
}

// fill in the missing exported types from css-tree
type Fragment = {
  nodes: List<CssNode>;
};

interface Lexer {
  findDeclarationValueFragments(declaration: Declaration, type: string, name: string): Fragment[];
}

/** Converts a list of Fragments into their values. */
function translateFragments(fragments: Fragment[]): string[] {
  return fragments.map((fragment: any) =>
    generate({
      type: 'Value',
      loc: undefined,
      children: fragment.nodes,
    }),
  );
}

/** Given a shorthand declaration like 'font', extract the longhand key/value pairs if they are set. */
function extractLonghandProperties(
  declaration: Declaration,
  keys: readonly string[],
): { name: string; value: string }[] {
  const result: { name: string; value: string }[] = [];
  for (const key of keys) {
    const values = translateFragments(
      (lexer as Lexer).findDeclarationValueFragments(declaration, 'Property', key),
    );
    if (values.length === 0) {
      continue;
    }
    result.push({
      name: key,
      value: values.join(' '),
    });
  }

  return result;
}

// the CSS property scroll-snap-type can change the scrolling behavior
// this also means that in playback we can't scroll smoothly
// so we will remove all of these declarations
// NOTE (eugenechang) same with property scroll-behavior
export function CSSScrollPropertiesTransform(): CSSTreeTransform {
  return {
    enter(
      this: WalkContext,
      node: CssNode,
      item: ListItem<CssNode>,
      inList: List<CssNode>,
    ): void {
      if (
        node.type !== 'Declaration' ||
        (
          node.property !== 'scroll-snap-type' &&
          node.property !== 'scroll-behavior'
        )
      ) {
        return;
      }

      inList.remove(item);
      if (this.block) {
        (this.block as PossiblyMutated<Block>)[mutatedTag] = true;
      } else {
        // if block isn't available, then we're likely rewriting an attribute, so mark all as mutated
        (this.root as PossiblyMutated<CssNode>)[mutatedTag] = true;
      }
    },
  };
}

/**
 * Rewrite 'display: inline' to 'display: var(--fs-display-inline)'.
 * 'fsstyles.raw.scss' contains rules that set this variable to 'inline' by
 * default, but 'inline-block' for excluded elements, so this transformation has
 * the effect of making 'display: inline' excluded elements render as 'display:
 * inline-block'.
 *
 * There's a subtle aspect of this design that's important to understand. We
 * could have also implemented this by saving the desired 'display:' value in a
 * CSS variable and applying it in 'fsstyles.raw.scss' using a rule like this:
 *
 *   [_fs_excluded='true'] {
 *     display: var(--fs-display-if-excluded);
 *   }

 * This works fine as long as we're able to rewrite all the CSS on the page.
 * However, if we have to load some CSS in fallback mode, this is not a good
 * approach, because there's no default value for '--fs-display-if-excluded'
 * that works in any situation. Using a default value like 'initial' or 'unset'
 * doesn't work right if the excluded element has an explicit 'display:' value.
 * Using a deliberately invalid default value has the same effect as 'initial'
 * because of the way that custom properties are evaluated. Obviously no
 * explicit value will work, either.
 *
 * For an edge case an incorrect default might be tolerable, but we really don't
 * want to break something as frequently-used and impactful as 'display' in
 * fallback mode, and that's why the implementation strategy below was chosen:
 * '--fs-display-inline' has no effect whatsoever unless we were able to rewrite
 * the site of the 'display' declaration, so rules from unrewritten stylesheets
 * will apply as normal.
 */
export function CSSDisplayInlineTransform(): CSSTreeTransform {
  return {
    enter(
      this: WalkContext,
      node: CssNode,
      item: ListItem<CssNode>,
      inList: List<CssNode>,
    ): void {
      if (node.type !== 'Declaration') {
        return;
      }
      if (!isExactlyDisplayInline(node)) {
        return;
      }

      const displayInlineReplacementDecl = createDeclaration(
        'display',
        createValue(createFunction('var', createIdentifier('--fs-display-inline'))),
        node.important,
      );

      insertDeclaration(this, displayInlineReplacementDecl, inList, item);
      inList.remove(item);
    },
  };
}

function isExactlyDisplayInline(declaration: Declaration): boolean {
  if (declaration.property !== 'display') {
    return false;
  }

  const value = declaration.value;
  if (value.type !== 'Value') {
    return false;
  }
  if (value.children.getSize() !== 1) {
    return false;
  }

  const child = value.children.first()!;
  if (child.type !== 'Identifier') {
    return false;
  }
  return child.name.toLowerCase() === 'inline';
}

function insertDeclaration(
  ctx: WalkContext,
  decl: Mutated<Declaration>,
  list: List<PossiblyMutated<CssNode>>,
  before: ListItem<CssNode> | null,
): void {
  list.insertData(decl, before!);
  if (ctx.block) {
    (ctx.block as PossiblyMutated<Block>)[mutatedTag] = true;
  } else {
    (ctx.root as PossiblyMutated<DeclarationList>)[mutatedTag] = true;
  }
}

// Serialize the provided css-tree AST by interpolating mutated subtrees into
// the original source code.
export function interpolateMutations(
  astRoot: CssNode,
  originalSource: string,
): string {
  let lastMutationStart = 0;
  let lastMutationEnd = 0;
  let currentMutatedSubtreeRoot: CssNode | undefined = undefined;
  const chunks: string[] = [];

  walk(astRoot, {
    enter(node: CssNode): void {
      // We serialize mutated subtrees at the root, which implicitly serializes
      // all the children. Don't bother visiting those children since we've
      // already handled them.
      if (currentMutatedSubtreeRoot) {
        return;
      }

      // If this node hasn't been mutated, don't visit it.
      if (!(mutatedTag in node)) {
        return;
      }

      // We need to know the source location of mutated nodes to figure out what
      // to copy from the original source.
      if (!node.loc) {
        throw new Error(`Root of mutated subtree must have a source location`);
      }

      const mutationStart = node.loc.start.offset;
      const mutationEnd = node.loc.end.offset;

      if (mutationStart === lastMutationStart) {
        // This is part of a sequence of mutations that map to the same original
        // source location. We don't need to copy anything from the original
        // source in this case.
      } else if (mutationStart === lastMutationEnd) {
        // This mutation comes right after a previous mutation. We don't need to
        // copy anything from the original source in this case.
      } else if (mutationStart > lastMutationEnd) {
        // Copy the original source between the end of the last mutation and the
        // start of this mutation.
        chunks.push(originalSource.substring(lastMutationEnd, mutationStart));
      } else {
        // TODO(sethf): Add the capability to send messages to Bugsnag from the
        // app and use it here. If we hit this case, it's unambiguously a bug.
        throw new Error(`New mutation starts before previous mutation`);
      }

      // Serialize the entire subtree under this node using css-tree's built-in
      // generate() function.
      const replacementSource = generate(node);
      chunks.push(replacementSource);

      lastMutationStart = mutationStart;
      lastMutationEnd = mutationEnd;
      currentMutatedSubtreeRoot = node;
    },

    leave(node: CssNode): void {
      if (node === currentMutatedSubtreeRoot) {
        // We're exiting the mutated subtree that we just serialized. Start
        // looking for a new mutated subtree.
        currentMutatedSubtreeRoot = undefined;
      }
    },
  });

  // If any of the original source hasn't been copied yet, grab it.
  if (lastMutationEnd < originalSource.length) {
    chunks.push(originalSource.substring(lastMutationEnd));
  }

  // Combine all the serialized chunks into a single string.
  return chunks.join('');
}

export function CSSFillUrlTransform(baseUrl: string): CSSTreeTransform {
  const parsedUrl = new URL(baseUrl);
  parsedUrl.hash = '';
  const urlWithoutHash = parsedUrl.href; // USVString with possibly encoded components
  return {
    enter(
      this: WalkContext,
      node: CssNode,
      item: ListItem<CssNode>,
      inList: List<CssNode>,
    ): void {
      if (
        node.type !== 'Url' ||
        this.declaration?.property !== 'fill'
      ) {
        return;
      }

      const matches = node.value.value.match(/^(['"]?)(.*?)['"]?$/);
      if (!matches) {
        return;
      }

      const quotes = matches[1];
      const url = matches[2];

      if (!url?.startsWith(`${urlWithoutHash}#`)) {
        return;
      }

      node.value.value = `${quotes}${url.substr(urlWithoutHash.length)}${quotes}`;
      (node as Mutated<Url>)[mutatedTag] = true;
    },
  };
}

/**
 * Returns a transform that quotes identifier around format
 * Safari cssText do not include quotes around format in @font-face {src: url(...) format(...)}
 * Chrome always expects it (as of 92); this pads the format with quotes (ch163530)
 * @returns
 */
export function CSSSafariFontFaceSrcTransformer(): CSSTreeTransform {
  return {
    enter(
      node: CssNode,
    ) {
      if (node.type !== 'Atrule' || node.name !== 'font-face') {
        return;
      }

      node.block?.children.forEach(decl => {
        // we only care about src Declarations
        if (decl.type !== 'Declaration' || decl.property !== 'src') {
          return;
        }

        const declValue = decl.value;
        if (declValue.type === 'Raw') {
          console.warn('Encountered raw value at @font-face declaration; unable to rewrite src format');
          return;
        }

        declValue.children.forEach(val => {
          // we only care about format Function
          if (val.type !== 'Function' || val.name !== 'format') {
            return;
          }

          // replace every identifier with a quoted string node
          val.children.forEach((originalItem, formatNode, formatList) => {
            if (originalItem.type !== 'Identifier') {
              return;
            }

            const replacementItem = createString(`"${originalItem.name}"`);
            const replacementNode = formatList.createItem(replacementItem);
            formatList.replace(formatNode, replacementNode);
            (val as Mutated<CssNode>)[mutatedTag] = true;
          });
        });
      });
    },
  };
}

/**
 * In order to work with display: inline elements, we have a CSS rule in fsstyles.raw.css that specifies
 * display: var(--fs-display-inline) to specific tags; if, however, customer has a `* { display: X }` rule,
 * our rule would end up override that; the purpose of this transform is to increase the specificity of
 * such a rule so that it would override our rules
 */
export function CSSWildcardDisplayTransform(): CSSTreeTransform {
  return {
    enter(node: CssNode) {
      if (node.type !== 'Rule' || node.prelude.type !== 'SelectorList') {
        return;
      }

      // continue iff any in the prelude selector list contains a selector that is only *
      const wildcardSelector = node.prelude.children.toArray().find(s => {
        if (s.type !== 'Selector' || s.children.getSize() !== 1) {
          return false;
        }

        const sel = s.children.first()!;  // can't be null because we checked children size
        return sel.type === 'TypeSelector' && sel.name === '*';
      });

      if (!wildcardSelector) {
        return;
      }

      node.block.children.forEach((decl, declNode, declList) => {
        // NOTE: it's unclear why we are skipping over declarations that have important set to true
        if (decl.type === 'Declaration' && decl.property === 'display' && !decl.important) {
          (node as Mutated<CssNode>)[mutatedTag] = true;
          const declValue = clone(decl.value);
          declValue.loc = undefined;
          const value = createValue(declValue);
          const displayInlineReplacementDecl = createDeclaration(
            '--fs-display-inline',
            value,
            decl.important,
          );
          insertDeclaration(this, displayInlineReplacementDecl, declList, declNode);
        }
      });
    },
  };
}

/**
 * Rewrite 'cursor:' to '--fs-cursor:'. We read this custom property in
 * PseudoClassManager to determine which fake cursor to display during playback.
 * By storing the cursor value in a custom property, we can safely override the
 * real 'cursor:' property in interactive situations without affecting playback
 * of mouse events.
 */
export function CSSCursorTransform(): CSSTreeTransform {
  return {
    enter(this: WalkContext, node: CssNode): void {
      if (node.type !== 'Declaration') {
        return;
      }
      if (node.property !== 'cursor') {
        return;
      }

      const declaration = node as Mutated<Declaration>;
      declaration.property = '--fs-cursor';
      declaration[mutatedTag] = true;
    },
  };
}

/**
 * Escape unescaped colons in a string (e.g. [ng:cloak] -> [ng\\:cloak]).
 */
function replaceUnescapedColon(str: string) {
  const originalParts = str.split(':');
  const newParts: string[] = [];

  for (let i = 0; i < originalParts.length - 1; i++) {
    const part = originalParts[i]!;
    if (part.slice(-1) === '\\') {
      // Alraedy escaped. Can leave as-is
      newParts.push(part);
    } else {
      newParts.push(`${part}\\`);
    }
  }

  // Add on last item separately to ensure we don't try to escape wrong item
  newParts.push(originalParts[originalParts.length - 1]!);

  return newParts.join(':');
}

/**
 * Rewrite AngularJS-style CSS class names to escape colons (e.g. '[ng:cloak]' -> '[ng\\:cloak]').
 * This is necessary since the browser expects them to be escaped.
 */
export function CSSSelectorsWithColonsTransform(): CSSTreeTransform {
  return {
    enter(this: WalkContext, node: CssNode): void {
      if (node.type === 'Identifier' && node.name?.includes(':')) {
        const updatedName = replaceUnescapedColon(node.name);
        if (updatedName !== node.name) {
          node.name = updatedName;
          (node as PossiblyMutated<Identifier>)[mutatedTag] = true;
        }
      }
    },
  };
}


export function MozToWebkitTransform(): CSSTreeTransform {
  return {
    enter(this: WalkContext, node: CssNode): void {
      if (node.type !== 'Declaration' || !this.block) return;
      if (!node.property.startsWith('-moz')) return;

      const prop = node.property.substring(4); // '-moz'.length
      const decl = createDeclaration(`-webkit${prop}`, createValue(node.value));
      insertDeclaration(this, decl, this.block.children, null);
    },
  };
}
